import { HttpParams } from '@angular/common/http';
import { AfterContentInit, Component, forwardRef, Inject, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ErrorMessages, Permissions } from '../../common/constants';
import { LocationService } from '../../dashboard-area/location/location.service';
import { WorkSession } from '../../model';
import { AuthenticationService } from '../../service/authentication.service';
import { ThingContextService } from '../../service/thing-context.service';
import { WorkSessionDefinitionService } from '../../service/work-session-definition.service';
import { PropertyComponent } from '../../shared/component';
import { DownloadProgressBarDialogComponent } from '../../shared/download-progress-bar-dialog/download-progress-bar-dialog';
import { DatetimeFormatterPipe, FileSizeFormatterPipe, LocalizationPipe } from '../../shared/pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { DetailsWidget } from '../shared/details-widget';
import { SnackbarService } from '../shared/snackbar.service';
import { WorkSessionEditDialogComponent } from '../work-session-edit-dialog/work-session-edit-dialog.component';
import { WorkSessionDetailsService } from './work-session-details.service';

@Component({
    selector: 'work-session-details-widget',
    template: require('./work-session-details.component.html'),
    styles: [require('../shared/details-widget.css')],
    providers: [WorkSessionDetailsService, FileSizeFormatterPipe, DatetimeFormatterPipe, LocationService]
})
export class WorkSessionDetailsComponent extends DetailsWidget<WorkSession> implements AfterContentInit {

    editableProperties: PropertyComponent[];

    constructor(
        @Inject(forwardRef(() => WorkSessionDetailsService)) protected workSessionDetailsService: WorkSessionDetailsService,
        @Inject(forwardRef(() => FileSizeFormatterPipe)) fileSizeFormatterPipe: FileSizeFormatterPipe,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) dateTimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => LocalizationPipe)) localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => ThingContextService)) private context: ThingContextService,
        @Inject(forwardRef(() => LocationService)) private locationService: LocationService,
        @Inject(forwardRef(() => WorkSessionDefinitionService)) private workSessionDefinitionService: WorkSessionDefinitionService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => ViewContainerRef)) vcRef: ViewContainerRef,
        @Inject(forwardRef(() => SnackbarService)) private snackbarService: SnackbarService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService
    ) {
        super(dateTimeFormatterPipe, fileSizeFormatterPipe, localizationPipe, workSessionDetailsService, dialog, vcRef);
    }

    ngOnInit(): void {
        this.element = this.context.getCurrentWorkSession();
        this.visible = !!this.element;
        if (this.visible) {
            this.objectContextId = this.element.id;
        }
        this.metrics = this.context.getMetrics();
        super.ngOnInit();
    }

    ngAfterContentInit(): void {
        this.getWorkSessionDefintion()
            .then(() => this.workSessionDetailsService.preloadPostComputationAlgorithms(this.rows))
            .then(() => {
                this.editableProperties = this.getEditableProperties();
                this.init();
            })
    }

    private init(): void {
        if (!this.element.location && this.element.locationId) {
            this.locationService.getLocationById(this.element.locationId).then(location => {
                this.element.location = location;
                this.initDetailsWidget();
            });
        } else {
            this.initDetailsWidget();
        }
    }

    private getWorkSessionDefintion(): Promise<void> {
        return this.workSessionDefinitionService.getWorkSessionDefinitionsById(this.element.workSessionDefinitionId).then(wsd => {
            this.workSessionDetailsService.setWorkSessionDefinition(wsd);
        }).catch(() => { /* do nothing */ });
    }

    initializationDataCallback() { }

    exportReport(): void {
        let body = {
            elementIds: [this.element.id]
        };
        const params = new HttpParams().set('thingId', this.element.thing.id);
        const dialogRef = this.openDownloadDialog();
        this.workSessionDetailsService.exportReport(body, params).then(() => {
            dialogRef.close();
        }).catch(err => {
            this.snackbarService.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR), 'notification-error', true);
            dialogRef.close();
        });
    }

    private openDownloadDialog(): any {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '10%';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = "download-progress-bar-dialog";
        dialogConfig.data = { title: "downloadsTitle" };
        return this.dialog.open(DownloadProgressBarDialogComponent, dialogConfig);
    }

    editWorkSession(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = "edit-historical-work-session-dialog";
        dialogConfig.width = '428px';
        dialogConfig.data = { properties: this.editableProperties, workSession: this.element };
        firstValueFrom(this.dialog.open(WorkSessionEditDialogComponent, dialogConfig).afterClosed()).then(updatedWs => {
            if (updatedWs) {
                this.element = updatedWs;
                this.init();
            }
        });
    }

    private getEditableProperties(): PropertyComponent[] {
        if (!this.authenticationService.checkPermission(Permissions.WRITE_WORK_SESSION) || !this.element.endTimestamp) {
            return [];
        }
        return this.rows?.filter(row => (row instanceof PropertyComponent) && row.editable && (((row.name.startsWith("metrics.") || row.name.startsWith("postComputationResults.")) && row.name.split(".").length == 3) || ['startTimestamp', 'endTimestamp'].includes(row.name) || (row.name.startsWith("properties."))));
    }

}